import { Button, Card, Col, Descriptions, Popover, Row, Space, Spin, Tooltip, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { DATETIME_FORMAT } from "../../../pages/features/PremierPlus/shared";
import { CheckCircleTwoTone, CloseCircleTwoTone, RedoOutlined } from "@ant-design/icons";
import { useGetPremierPlusBinDto } from "src/queries/useGetPremierPlusBinDto";
import { useEcDeviceQuery, useOfflineReport } from "src/components/BinDetails";
import CustomTag from "src/components/CustomTag";
import { CheckFailedIcon, CheckPassedIcon } from "src/pages/features/WeatherMonitorAntdOnly";
import { useQueryClient } from "@tanstack/react-query";
import { binDBKeys } from 'src/pages/binOverview/BinCommander';
import BinInfoDTO from "src/models/BinInfoDTO";
import Routes from "src/consts/Routes";
import { useHistory } from "react-router";
import { HistoryState } from "src/pages/dashboard/BinStatusPage/BinStatsPage";
import Canvas from "src/pages/dashboard/HomeScreen/Canvas";
import { BinProductTag } from "src/components/BinProductDescriptionTag";
import { PRIMARY_BLUE_SHIVVERS } from "src/app/theme";
import { OverviewCardTag } from "src/pages/features/Premier/ShivversTag";
import { BIN_DETAILS_TOOLTIP_TEXT, BinDetailsButton } from "src/components/BinDetailsButton";
import { formatBool, formatNumber } from "src/pages/dashboard/BinStatusPage/HeaterControls";
import { RefreshButtonNew } from "src/components/RefreshButtonNew";
import { BinDetailsStyledCard } from "src/components/BinDetailsStyledCard";


interface PremierPlusOverviewProps {
    layout: "horizontal" | "vertical"; 
    binId: number;
    binName: string;
    deviceId: string;
    binInfo: BinInfoDTO;
}

const { Text } = Typography;

const PremierPlusOverview : React.FC<PremierPlusOverviewProps> = ({ binId, binName, deviceId, layout, binInfo }) => {

    const [content, setContent] = useState<React.ReactNode>();
    const queryClient = useQueryClient();
    
    const ecDeviceQuery = useEcDeviceQuery(deviceId, true);
    const { getPremierPlusBinDtoQuery } = useGetPremierPlusBinDto(binId, { enabled: true, refetchOnWindowFocus: false, retry: false });
    const getOfflineConnectionReport = useOfflineReport(deviceId, false);
    const captureTimeFormatted = getPremierPlusBinDtoQuery.data?.captureTimeUtc != null ? `Last Tape Reading: ${dayjs(getPremierPlusBinDtoQuery.data?.captureTimeUtc)?.format(DATETIME_FORMAT)}` : null;

    useEffect( () => {
        if(getPremierPlusBinDtoQuery.data){
            setContent(
                <>
                    <Descriptions bordered layout={layout} size="small" column={1} labelStyle={{fontSize: 11}} contentStyle={{fontSize: 11}}>
                        <Descriptions.Item label={'Relative Humidity'}>
                            {formatNumber(getPremierPlusBinDtoQuery.data?.averageAmbientRhtSensor_RhPercent, {decimalPlaces: 1})}
                        </Descriptions.Item>

                        <Descriptions.Item label={'Avg. Moisture'}>
                            {formatNumber(getPremierPlusBinDtoQuery.data?.dryingOperation?.averageSampleMc, {decimalPlaces: 1})}
                        </Descriptions.Item>

                        <Descriptions.Item label={'Grain Temp'}>
                            {formatNumber(getPremierPlusBinDtoQuery.data?.dryingOperation?.lastSampleTemperatureF, {decimalPlaces: 1})}
                        </Descriptions.Item>

                        <Descriptions.Item label={'Plenum Temp'}>
                            {formatNumber(getPremierPlusBinDtoQuery.data?.averagePlenumRhtSensor_TemperatureF, {decimalPlaces: 1})}
                        </Descriptions.Item>

                        <Descriptions.Item label={'Target Temp'}>
                            {formatNumber(getPremierPlusBinDtoQuery.data?.dryingOperation?.currentTargetTemperatureF, {decimalPlaces: 1})}
                        </Descriptions.Item>

                        <Descriptions.Item label={'Machine'}>
                            <Space direction="horizontal" wrap size="small">
                                {formatBool(getPremierPlusBinDtoQuery.data?.isMachineOn, {
                                    null: "--",
                                    false: "OFF",
                                    true: "ON",
                                    bold: false,
                                })}
                                {getPremierPlusBinDtoQuery.data?.isMachineOn == null ? null : getPremierPlusBinDtoQuery.data?.isMachineOn === true ? <CheckPassedIcon /> : <CheckFailedIcon />}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>
                </>
            );
        }else{
            getOfflineConnectionReport.refetch();
        }
    }, [getPremierPlusBinDtoQuery.dataUpdatedAt]);
    
    const offlineContext = useMemo( () => {

        if(getOfflineConnectionReport.data?.connectionState != 'Connected'){
            return(
                <>
                    <Row justify={'center'}> 
                        <CustomTag 
                        label={'SYSTEM OFFLINE'}
                        color='red'
                        />
                    </Row>
                    <Row>
                        <Col>
                            <p>Last Time connected: {dayjs(getOfflineConnectionReport.data?.moduleTwinLastActivityTime).format(DATETIME_FORMAT)}</p>
                            <p>Device is connected? {ecDeviceQuery.data?.context?.connection?.status ?? 'false'}</p>
                        </Col>
                    </Row>
                </>
            )
        }

        return null;

    }, [getOfflineConnectionReport.dataUpdatedAt, ecDeviceQuery.dataUpdatedAt]);

    const history = useHistory<HistoryState>();

    const handleClickDetails = () => {

        queryClient.setQueriesData(binDBKeys.binInfo(binId), (oldData: any) => {
            if (binId === undefined) {
                return oldData;
            }

            return binInfo;
        });
        const binStatsRoute = `${Routes.BIN_STATS}/${binInfo.id}`;

        history.push(binStatsRoute, {
            binInfo: binInfo,
        })
    }

    return (
        <BinDetailsStyledCard
        loading={getPremierPlusBinDtoQuery.isLoading}
        title={
            <div className="premier-plus-overview-title">
                <Row justify="start" align="middle">
                    <Col>
                        <Space direction='horizontal' wrap size={"small"}>
                            <span>{binName}</span>
                            <OverviewCardTag>Premier+</OverviewCardTag>
                        </Space>
                    </Col>
                </Row>
                <Row gutter={[4, 0]}>
                    <Col>
                        {
                            getOfflineConnectionReport.isLoading ? (
                                <Tooltip placement="bottom" title="Checking bin status." trigger="hover">
                                    <Spin size="small" />
                                </Tooltip>
                            ) : getOfflineConnectionReport.data?.connectionState === "Connected" ? (
                                <Tooltip title="Connected to bin." trigger="hover">
                                    <CheckCircleTwoTone twoToneColor="#52c41a" />
                                </Tooltip>
                            ) : (
                                <Popover
                                    title="Failed to connect to bin."
                                    content={offlineContext}
                                    trigger="hover"
                                >
                                    <CloseCircleTwoTone twoToneColor="#eb2f96" />
                                </Popover>
                            )
                        }                   
                    </Col>
                    <Col>
                        <span style={{fontWeight: 500, color: "white",
                        visibility: getPremierPlusBinDtoQuery.isFetching ? 'hidden' : undefined,
                        fontSize: 11,
                        }}>
                            {captureTimeFormatted}
                        </span>
                    </Col>
                </Row>
            </div>
        }
        extra={
            <Row gutter={8} justify="end">
                <Col>
                    <RefreshButtonNew 
                        onClick={() => {getPremierPlusBinDtoQuery.refetch()}}
                        loading={getPremierPlusBinDtoQuery.isLoading}
                        disabled={getPremierPlusBinDtoQuery.isLoading}
                    />
                </Col>
                <Col>
                    <BinDetailsButton
                    onClick={handleClickDetails}
                    >
                    </BinDetailsButton>
                </Col>
            </Row>
        }>
            <Row justify={"center"} align="stretch">
                <Col xs={24} sm={12}>
                    <Tooltip title={BIN_DETAILS_TOOLTIP_TEXT} placement="bottom">
                        <a onClick={handleClickDetails}>
                            <Canvas binData={null!} productTypeOverride="Premier+" />
                        </a>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={12}>
                    {content}
                </Col>
            </Row>
        </BinDetailsStyledCard>
    )
}

export default PremierPlusOverview;